import { lusolve } from 'mathjs';

export type PerspectivePoint = readonly [number, number];

/** CSS homography mapping the four source corners to their picked destinations. */
export function computePerspectiveTransform(
  source: readonly PerspectivePoint[],
  destination: readonly PerspectivePoint[],
): string {
  if (
    source.length !== 4 ||
    destination.length !== 4 ||
    [...source, ...destination].some((point) =>
      point.some((coordinate) => !Number.isFinite(coordinate)),
    )
  ) {
    throw new RangeError(
      'A perspective transform requires four finite point pairs.',
    );
  }

  const coefficients: number[][] = [];
  const values: number[] = [];
  for (let index = 0; index < 4; index += 1) {
    const [x, y] = source[index];
    const [targetX, targetY] = destination[index];
    coefficients.push([x, y, 1, 0, 0, 0, -x * targetX, -y * targetX]);
    coefficients.push([0, 0, 0, x, y, 1, -x * targetY, -y * targetY]);
    values.push(targetX, targetY);
  }

  const solution = lusolve(coefficients, values).map((row) => {
    if (
      !Array.isArray(row) ||
      typeof row[0] !== 'number' ||
      !Number.isFinite(row[0])
    ) {
      throw new RangeError('The perspective transform has no finite solution.');
    }
    return row[0];
  });

  // CSS matrix3d uses column-major homogeneous coordinates; Z is unchanged.
  return `matrix3d(${[
    solution[0],
    solution[3],
    0,
    solution[6],
    solution[1],
    solution[4],
    0,
    solution[7],
    0,
    0,
    1,
    0,
    solution[2],
    solution[5],
    0,
    1,
  ].join(',')})`;
}
